import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Divider,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  Store as StoreIcon,
  AdminPanelSettings as AdminIcon,
  EventNote as BookingIcon,
  Restaurant as RestaurantIcon,
  Print as PrintIcon,
  QrCode as QrCodeIcon,
  PhoneAndroid as PhoneAndroidIcon,
  CalendarMonth as CalendarMonthIcon,
  Block as BlockIcon,
} from '@mui/icons-material';

const Documentation: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h3" gutterBottom>
        予約プラットフォーム システムドキュメント
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Reservation Platform System Documentation
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* システム概要 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          システム概要
        </Typography>
        <Typography variant="body1" paragraph>
          本システムは、多業種対応型の予約管理プラットフォームです。美容室、サロン、飲食店、医療機関など、
          様々な業種の予約業務を効率化するための4つのアプリケーションで構成されています。
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>本番環境稼働中</strong>
          <br />
          すべてのアプリケーションはFirebase Hostingにデプロイされ、本番環境で利用可能です。
          <br />
          スタッフアプリ（Flutter）はApp Store / Google Playで配信中です。
          <br />
          最終更新: 2025年12月9日
        </Alert>
      </Paper>

      {/* アプリケーション一覧 */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        アプリケーション構成
      </Typography>

      {/* ランディングページ */}
      <Card sx={{ mb: 3, bgcolor: 'info.50' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <BookingIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
            <Box>
              <Typography variant="h6">ランディングページ</Typography>
              <Chip label="Landing Page" color="info" size="small" sx={{ mt: 0.5 }} />
            </Box>
          </Box>
          <Typography variant="body2" paragraph>
            一般向けのサービス紹介ページ。機能説明、料金プラン、対応業種などを掲載。
          </Typography>
          <Typography variant="caption" display="block" gutterBottom>
            <strong>URL:</strong>
          </Typography>
          <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all', fontWeight: 'bold', color: 'info.main' }}>
            https://easymenu-app.com
          </Typography>
        </CardContent>
      </Card>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AdminIcon sx={{ fontSize: 40, color: 'primary.main', mr: 1 }} />
                <Typography variant="h6">プラットフォーム管理</Typography>
              </Box>
              <Chip label="Platform Admin" color="primary" size="small" sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                システム全体を管理する管理者用アプリケーション
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                <strong>URL:</strong>
              </Typography>
              <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all', mb: 1 }}>
                https://easymenu-app.com/admin
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                <strong>機能:</strong>
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="店舗管理" secondary="店舗の追加・編集・削除" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="ユーザー管理" secondary="統合ユーザーシステム管理" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="業種管理" secondary="業種マスタ・機能設定" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="プラン管理" secondary="料金プランの設定" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="システムドキュメント" secondary="本ページの管理" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StoreIcon sx={{ fontSize: 40, color: 'success.main', mr: 1 }} />
                <Typography variant="h6">店舗ダッシュボード</Typography>
              </Box>
              <Chip label="Shop Dashboard" color="success" size="small" sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                各店舗オーナー向けの管理アプリケーション
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                <strong>URL:</strong>
              </Typography>
              <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all', mb: 1 }}>
                https://easymenu-app.com/staff-dashboard
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                <strong>機能:</strong>
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        予約管理 <Chip label="実装済み" color="success" size="small" />
                      </Box>
                    }
                    secondary="月・週・日・リスト表示、予約CRUD、リソース管理、自動リマインダー（LINE/SMS/メール）"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        顧客管理 <Chip label="実装済み" color="success" size="small" />
                      </Box>
                    }
                    secondary="顧客情報、予約履歴、来店回数"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        従業員管理 <Chip label="実装済み" color="success" size="small" />
                      </Box>
                    }
                    secondary="スタッフ登録、担当サービス設定"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        シフト・勤怠管理 <Chip label="実装済み" color="success" size="small" />
                      </Box>
                    }
                    secondary="シフト登録、出退勤記録、勤務時間集計"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        メニュー・プラン設定 <Chip label="実装済み" color="success" size="small" />
                      </Box>
                    }
                    secondary="サービス登録、料金設定、事前決済設定"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        売上分析 <Chip label="実装済み" color="success" size="small" />
                      </Box>
                    }
                    secondary="日次・月次売上、予約統計、ダッシュボード"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Stripe Connect <Chip label="実装済み" color="success" size="small" />
                      </Box>
                    }
                    secondary="店舗別決済、オンボーディング、入金管理"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        LINE連携 <Chip label="実装済み" color="success" size="small" />
                      </Box>
                    }
                    secondary="LINE Messaging API、予約通知、顧客とのチャット"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        モバイルオーダー <Chip label="実装済み" color="success" size="small" />
                      </Box>
                    }
                    secondary="商品管理、注文管理、会計（モバイルオーダー/予約/POS）、プリンター連携（飲食店のみ）"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        統合ユーザーシステム <Chip label="実装済み" color="success" size="small" />
                      </Box>
                    }
                    secondary="複数店舗管理、ロールベース権限、店舗切替、複数店舗統合ダッシュボード"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        業種別機能制御 <Chip label="実装済み" color="success" size="small" />
                      </Box>
                    }
                    secondary="業種に応じたメニュー自動制御"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        営業時間設定 <Chip label="実装済み" color="success" size="small" />
                      </Box>
                    }
                    secondary="営業カレンダー、定休日、曜日別営業時間設定"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BookingIcon sx={{ fontSize: 40, color: 'warning.main', mr: 1 }} />
                <Typography variant="h6">顧客予約</Typography>
              </Box>
              <Chip label="Customer Booking" color="warning" size="small" sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                エンドユーザー向けの予約・注文アプリケーション
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                <strong>URL:</strong>
              </Typography>
              <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all', mb: 1 }}>
                https://easymenu-app.com/booking
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                <strong>機能:</strong>
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="店舗検索" secondary="店舗コードまたはQRコードで検索" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="予約機能" secondary="日時選択、スタッフ指名、メニュー選択" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="モバイルオーダー" secondary="QRコードで注文（飲食店）" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="事前決済" secondary="Stripeによるオンライン決済" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="予約履歴" secondary="過去の予約・注文確認" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="多言語対応" secondary="日本語・英語・タイ語・中国語・韓国語" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* スタッフアプリ（Flutter） */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'secondary.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PhoneAndroidIcon sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
          <Box>
            <Typography variant="h5">スタッフアプリ（Flutter）</Typography>
            <Chip label="iOS / Android" color="secondary" size="small" sx={{ mt: 0.5 }} />
          </Box>
        </Box>
        <Typography variant="body1" paragraph>
          店舗スタッフ向けのネイティブモバイルアプリケーション。予約管理、商品管理、売り切れ設定などを
          スマートフォンから操作できます。App Store / Google Playで配信中。
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          主要機能
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarMonthIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="subtitle1">予約管理</Typography>
                </Box>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="カレンダー表示"
                      secondary="週表示・月表示で予約を確認"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="ガントチャート"
                      secondary="縦軸：席/テーブル、横軸：時間で視覚的に管理"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          ブロック時間設定 <Chip label="NEW" color="error" size="small" />
                        </Box>
                      }
                      secondary="休憩時間や準備時間を予約不可に設定"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="予約承認"
                      secondary="保留中の予約を承認/拒否"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <RestaurantIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="subtitle1">商品・注文管理</Typography>
                </Box>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="商品管理"
                      secondary="商品の追加・編集・削除（owner/managerのみ）"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="売り切れ設定"
                      secondary="ワンタップで売り切れ/販売中を切り替え"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="カテゴリ・オプション管理"
                      secondary="商品カテゴリとオプションの設定"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="注文確認"
                      secondary="リアルタイムで注文を確認・処理"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AdminIcon sx={{ mr: 1, color: 'warning.main' }} />
                  <Typography variant="subtitle1">ロールベースアクセス制御</Typography>
                </Box>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="オーナー (owner)"
                      secondary="全機能にアクセス可能、スタッフ管理、設定変更"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="マネージャー (manager)"
                      secondary="商品管理、予約管理、スタッフシフト確認"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="スタッフ (staff)"
                      secondary="自分の予約確認、売り切れ設定のみ"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <StoreIcon sx={{ mr: 1, color: 'info.main' }} />
                  <Typography variant="subtitle1">その他の機能</Typography>
                </Box>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="多言語対応"
                      secondary="日本語・英語・タイ語"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="プッシュ通知"
                      secondary="新規予約・注文の通知"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="オフライン対応"
                      secondary="一部機能はオフラインでも動作"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="シフト確認"
                      secondary="自分のシフトスケジュールを確認"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            技術スタック
          </Typography>
          <Typography variant="body2">
            • Flutter 3.x / Dart<br />
            • Riverpod（状態管理）<br />
            • go_router（ルーティング）<br />
            • Firebase Auth / Firestore / Cloud Messaging
          </Typography>
        </Alert>
      </Paper>

      {/* ログイン情報 */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h5" gutterBottom>
          ログイン情報
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          以下はテスト環境用のログイン情報です。本番環境では必ず変更してください。
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  プラットフォーム管理者
                </Typography>
                <Typography variant="body2">
                  <strong>メールアドレス:</strong> admin@platform.test
                </Typography>
                <Typography variant="body2">
                  <strong>パスワード:</strong> admin123456
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  全システムの管理権限を持つアカウント
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  店舗オーナー（サンプル）
                </Typography>
                <Typography variant="body2">
                  <strong>メールアドレス:</strong> shop@test.com
                </Typography>
                <Typography variant="body2">
                  <strong>パスワード:</strong> shop123456
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  店舗管理用のサンプルアカウント
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* 統合ユーザーシステム */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AdminIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Typography variant="h5">
            統合ユーザーシステム
          </Typography>
        </Box>
        <Chip label="実装済み" color="success" sx={{ mb: 2 }} />
        <Typography variant="body1" paragraph>
          2025年11月に実装された統合ユーザーシステムにより、店舗オーナー、スタッフ、顧客を
          一元管理できるようになりました。複数店舗の運営や権限管理が大幅に改善されています。
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          主要機能
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  複数店舗アクセス管理 (shopAccess)
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="1ユーザー複数店舗"
                      secondary="1つのアカウントで複数店舗を管理可能"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="店舗別ロール設定"
                      secondary="店舗ごとにowner/manager/staffの役割を設定"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="店舗切り替え"
                      secondary="ダッシュボードから簡単に店舗を切り替え"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="アクセス履歴"
                      secondary="いつ誰が権限を付与したか記録"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  ロールベース権限制御
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="systemRole: platformAdmin"
                      secondary="プラットフォーム全体の管理権限"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="systemRole: business"
                      secondary="店舗オーナー・スタッフ権限"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="systemRole: customer"
                      secondary="一般顧客の予約・注文権限"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="メニュー表示制御"
                      secondary="ロールに応じて表示するメニューを自動調整"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          データ構造
        </Typography>
        <Card>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              usersコレクション
            </Typography>
            <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
              {`{
  id: string,
  email: string,
  displayName: string,
  systemRole: 'platformAdmin' | 'business' | 'customer',
  organizationId?: string,
  shopAccess: [
    {
      shopId: string,
      role: 'owner' | 'manager' | 'staff',
      grantedAt: Timestamp,
      grantedBy: string
    }
  ],
  isActive: boolean,
  isSuspended: boolean
}`}
            </Typography>
          </CardContent>
        </Card>

        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            データ移行機能
          </Typography>
          <Typography variant="body2">
            既存の店舗オーナーデータを統合ユーザーシステムに移行する機能を実装。
            /migrateページから実行可能。後方互換性も維持しており、旧データでも動作します。
          </Typography>
        </Alert>
      </Paper>

      {/* 業種別機能制御 */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'info.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <StoreIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
          <Typography variant="h5">
            業種別機能制御
          </Typography>
        </Box>
        <Chip label="実装済み" color="success" sx={{ mb: 2 }} />
        <Typography variant="body1" paragraph>
          業種マスタ（businessTypes）の設定に基づき、店舗の業種に応じて利用可能な機能を
          自動的に制御します。例えば、美容室ではモバイルオーダー機能が非表示になります。
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          対応業種と機能
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  飲食店 (Code: 41)
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="✓ 予約管理"
                      secondary="テーブル予約、コース予約"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="✓ モバイルオーダー・POS"
                      secondary="QR注文、POSレジ、キッチンプリンター連携"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="✓ テーブル管理"
                      secondary="テーブル配置、QRコード生成"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="✓ スタッフ管理"
                      secondary="シフト、勤怠管理"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  美容室・理容室 (Code: 21)
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="✓ 予約管理"
                      secondary="スタッフ指名予約、メニュー選択"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="✓ スタッフ管理"
                      secondary="シフト、勤怠管理"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="× モバイルオーダー"
                      secondary="表示されません"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="× テーブル管理"
                      secondary="表示されません"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          業種マスタ設定
        </Typography>
        <Card>
          <CardContent>
            <Typography variant="body2" paragraph>
              Admin画面（/business-types）から業種ごとの機能設定を管理できます。
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              businessTypesコレクション
            </Typography>
            <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
              {`{
  code: '41',
  name: '飲食店',
  mobileOrderEnabled: true,
  features: {
    reservation: true,
    mobileOrder: true,
    staffManagement: true,
    roomManagement: false,
    tableManagement: true
  }
}`}
            </Typography>
          </CardContent>
        </Card>
      </Paper>

      {/* モバイルオーダー機能 */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'success.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <RestaurantIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
          <Typography variant="h5">
            モバイルオーダー機能
          </Typography>
        </Box>
        <Chip label="実装済み（飲食店のみ）" color="success" sx={{ mb: 2 }} />
        <Typography variant="body1" paragraph>
          飲食店向けのモバイルオーダー機能が実装されました。既存の予約システムを活用し、
          QRコードによる注文、キッチンプリンター連携、POS機能を提供しています。
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          主要機能
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <QrCodeIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">注文システム</Typography>
                </Box>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="QRコードスキャン"
                      secondary="テーブルごとにQRコード生成・スキャンで注文"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="商品カテゴリ管理"
                      secondary="前菜、メイン、デザート、ドリンク等"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="オプション設定"
                      secondary="サイズ、トッピング、調理方法等"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="リアルタイム注文"
                      secondary="顧客スマホから直接注文・送信"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PrintIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Typography variant="h6">キッチンプリンター</Typography>
                </Box>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="SIIプリンター対応"
                      secondary="SII製キッチンプリンターとの連携"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="複数プリンター設定"
                      secondary="調理場、バー、デザート等の担当別"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="自動印刷"
                      secondary="注文受付時に自動でキッチンに印刷"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="注文伝票フォーマット"
                      secondary="テーブル番号、商品名、オプション表示"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          POS・会計機能
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  会計管理（3つのモード対応）
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="モバイルオーダー会計"
                      secondary="テーブル別注文管理、分割会計対応"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="予約会計"
                      secondary="予約来店者の会計、施術後の会計"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="POSレジモード 🆕"
                      secondary="メニュー選択→直接会計、テイクアウト、レジ前商品、物販対応"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="割引対応"
                      secondary="固定額・パーセント割引"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="レシート印刷"
                      secondary="プリンター連携、会計モード別レシート"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="売上集計"
                      secondary="日次・月次レポート、会計履歴管理"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  商品管理
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="メニュー登録"
                      secondary="商品名、価格、写真、説明"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="在庫管理"
                      secondary="品切れ設定・自動非表示"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="時間帯別メニュー"
                      secondary="ランチ・ディナーの切り替え"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="多言語対応"
                      secondary="日本語・英語・タイ語"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          LINE連携機能
        </Typography>
        <Card>
          <CardContent>
            <Typography variant="body2" paragraph>
              LINE公式アカウントと連携し、LINEアプリ内でのモバイルオーダーを実現します。
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      LINE Mini App <Chip label="実装済み" color="success" size="small" />
                    </Box>
                  }
                  secondary="LINE内で完結する注文体験、LIFF v2.0対応"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="リッチメニュー"
                  secondary="LINEトーク画面下部にメニュー表示"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="注文通知"
                  secondary="LINE Messaging APIで注文確認送信"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="会員管理"
                  secondary="LINE友だち登録で顧客管理"
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          技術仕様
        </Typography>
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  プリンター連携
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="対応プリンター: SIIシリーズ"
                      secondary="RP-F10, RP-E11等のキッチンプリンター"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="接続方式: LAN/Wi-Fi"
                      secondary="ネットワーク経由での印刷指示"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="印刷プロトコル: ESC/POS"
                      secondary="業界標準の印刷コマンド"
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  データベース設計
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="categories コレクション: 商品カテゴリ" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="products コレクション: 商品マスタ" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="options コレクション: オプション設定" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="orders コレクション: 注文データ" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="printers コレクション: プリンター設定" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="tables コレクション: テーブル管理" />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            開発履歴（2025年）
          </Typography>
          <Typography variant="body2" component="div">
            <strong>2025年1月</strong><br />
            ✅ モバイルオーダー基本機能（商品管理、注文、会計）<br />
            ✅ キッチンプリンター連携（SIIプリンター対応）<br />
            ✅ QRコード注文システム<br />
            ✅ Stripe Connect店舗別決済<br />
            <br />
            <strong>2025年11月</strong><br />
            ✅ 統合ユーザーシステム実装<br />
            ✅ 複数店舗アクセス管理（shopAccess配列）<br />
            ✅ ロールベース権限制御（owner/manager/staff）<br />
            ✅ 業種別機能制御システム<br />
            ✅ 業種マスタによる自動メニュー制御<br />
            ✅ データ移行機能（レガシーシステムからの移行）<br />
            <br />
            <strong>2025年12月</strong><br />
            ✅ スタッフアプリ（Flutter）リリース（iOS / Android）<br />
            ✅ 予約カレンダー拡張（週表示・月表示・ガントチャート）<br />
            ✅ ブロック時間設定機能（予約不可時間帯の設定）<br />
            ✅ スタッフフィルター機能（担当者別予約表示）<br />
            ✅ アプリ・WEB間のロールベースアクセス制御統一<br />
            ✅ employeesコレクション統一（アプリ・WEB間）<br />
            <br />
            <strong>今後の予定</strong><br />
            📋 在庫管理機能の強化<br />
            📋 ポイントシステム<br />
            📋 AIによる需要予測機能<br />
            📋 リマインダー送信時刻のカスタマイズ
          </Typography>
        </Alert>
      </Paper>

      {/* 技術スタック */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          技術スタック
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  フロントエンド
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="React 18" secondary="UIライブラリ" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="TypeScript" secondary="型安全な開発" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Material-UI v7" secondary="UIコンポーネント" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="React Router" secondary="ルーティング" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Recharts" secondary="グラフ描画" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  バックエンド
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Firebase Authentication" secondary="ユーザー認証" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Cloud Firestore" secondary="NoSQLデータベース" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Firebase Storage" secondary="ファイルストレージ" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Cloud Functions" secondary="サーバーレス関数" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Firebase Hosting" secondary="Webホスティング" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* データ構造 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          主要データ構造
        </Typography>
        <Typography variant="body2" paragraph>
          Firestoreに格納されている主要なコレクションとその役割
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  users（統合ユーザー）
                </Typography>
                <Typography variant="caption" display="block">
                  全ユーザーの統合管理。systemRole、shopAccess配列による権限制御
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  shops（店舗）
                </Typography>
                <Typography variant="caption" display="block">
                  店舗情報、businessTypeCode、プラン、Stripe Connect設定
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  businessTypes（業種マスタ）
                </Typography>
                <Typography variant="caption" display="block">
                  業種定義、機能フラグ、手数料率（mobileOrderEnabled等）
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  reservations（予約）
                </Typography>
                <Typography variant="caption" display="block">
                  予約データ、日時、メニュー、スタッフ、支払い情報
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  customers（顧客）
                </Typography>
                <Typography variant="caption" display="block">
                  顧客情報、予約履歴、来店回数、LINE連携
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  menus（メニュー）
                </Typography>
                <Typography variant="caption" display="block">
                  サービス・料金、事前決済設定、所要時間
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  employees（従業員）
                </Typography>
                <Typography variant="caption" display="block">
                  スタッフ情報、担当サービス、シフト、勤怠
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  categories（商品カテゴリ）
                </Typography>
                <Typography variant="caption" display="block">
                  モバイルオーダー用カテゴリ（前菜、メイン、ドリンク等）
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  products（商品）
                </Typography>
                <Typography variant="caption" display="block">
                  飲食店メニュー、価格、在庫、多言語対応
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  orders（注文）
                </Typography>
                <Typography variant="caption" display="block">
                  モバイルオーダー注文データ、テーブル、支払いステータス
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  printers（プリンター）
                </Typography>
                <Typography variant="caption" display="block">
                  キッチンプリンター設定、IPアドレス、担当カテゴリ
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  tables（テーブル）
                </Typography>
                <Typography variant="caption" display="block">
                  テーブル配置、QRコード、座席数、エリア
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  blockedSlots（ブロック時間）
                </Typography>
                <Typography variant="caption" display="block">
                  予約不可時間帯、休憩時間、準備時間の設定
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* フッター */}
      <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
        <Typography variant="body2">
          Reservation Platform v2.1.0
        </Typography>
        <Typography variant="caption" display="block">
          Built with React 18, TypeScript, Material-UI v7, Firebase, and Flutter
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          最終更新: 2025年12月9日 | スタッフアプリ・予約カレンダー拡張・ブロック時間設定 実装済み
        </Typography>
      </Box>
    </Container>
  );
};

export default Documentation;
